'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Insight {
  id: string;
  type: 'pre_game' | 'trend' | 'historical' | 'contextual' | 'hidden_trend';
  match?: string;
  title: string;
  summary: string;
  details: string[];
  confidence: number;
  tags: string[];
  timestamp: string;
}

const INSIGHTS: Insight[] = [
  {
    id: '1',
    type: 'pre_game',
    match: 'TOR vs BOS',
    title: 'Analyse pré-match — Toronto Maple Leafs vs Boston Bruins',
    summary: 'Toronto présente une avantage xGoals significatif (+0.87/match) sur les 10 derniers matchs, porté par une ligne Marner-Matthews-Nylander en forme exceptionnelle. Boston souffre d\'une fatigue défensive notable après 3 matchs en 4 jours.',
    details: [
      'xGoals TOR L10: 3.21 vs BOS L10: 2.34 — Avantage TOR: +0.87',
      'Corsi% TOR: 54.2% — Domination possession attendue',
      'PP% TOR: 24.3% vs PK% BOS: 77.9% — Avantage jeu de puissance TOR',
      'Fatigue BOS: Score 3.8/5 — 3ème match en 4 jours',
      'Samsonov SV% L5: .931 — Performance gardien solide',
      'Historique H2H: TOR 7-3 sur les 10 derniers matchs',
    ],
    confidence: 81.2,
    tags: ['xGoals', 'Fatigue', 'PP%', 'H2H'],
    timestamp: '21:45',
  },
  {
    id: '2',
    type: 'hidden_trend',
    match: 'COL vs DAL',
    title: 'Tendance cachée détectée — Colorado Avalanche',
    summary: 'Analyse algorithmique révèle une corrélation forte (r=0.87) entre les performances de MacKinnon en power play et le taux de victoire COL. Sur les 15 derniers matchs avec MacKinnon PP actif, COL affiche 13-2.',
    details: [
      'Corrélation MacKinnon PP actif → Win Rate: r=0.87 (p<0.001)',
      'COL avec MacKinnon PP: 13-2 (L15) = 86.7% win rate',
      'COL sans MacKinnon PP: 6-9 (L15) = 40.0% win rate',
      'PP% COL avec MacKinnon: 31.2% vs sans: 18.4%',
      'xGoals PP COL: 1.23/match — Top 2 ligue',
      'Signal: MacKinnon confirmé PP ce soir → Boost +12.3% win prob',
    ],
    confidence: 87.4,
    tags: ['Tendance cachée', 'PP', 'MacKinnon', 'Corrélation'],
    timestamp: '21:30',
  },
  {
    id: '3',
    type: 'historical',
    match: 'EDM vs VGK',
    title: 'Comparaison historique — Edmonton Oilers vs Vegas Golden Knights',
    summary: 'Analyse des 3 dernières saisons révèle qu\'Edmonton surperforme systématiquement contre Vegas en saison régulière (+8.2% vs probabilité attendue) mais sous-performe en playoffs (-12.4%). Contexte actuel: saison régulière.',
    details: [
      'EDM vs VGK saison régulière (3 ans): 18-12 = 60.0% win rate',
      'EDM vs VGK playoffs (3 ans): 4-8 = 33.3% win rate',
      'Modèle prédit: 51.2% — Ajustement historique: +8.2% → 59.4%',
      'Skinner vs Hill: Avantage gardien EDM (+2.3% SV%)',
      'Draisaitl: 8 buts en 10 matchs vs VGK (historique)',
      'Contexte: Saison régulière → Facteur historique favorable EDM',
    ],
    confidence: 74.6,
    tags: ['Historique', 'H2H', 'Playoffs vs Saison', 'Gardien'],
    timestamp: '21:15',
  },
  {
    id: '4',
    type: 'contextual',
    match: 'FLA vs NYR',
    title: 'Analyse contextuelle — Florida Panthers',
    summary: 'Florida joue son 4ème match en 6 jours avec 2 déplacements. L\'analyse contextuelle indique une dégradation statistique significative dans ces conditions: -6.8% win rate, -0.43 xGoals, +0.31 xGoals concédés.',
    details: [
      'Fatigue calendrier FLA: Score 4.2/5 — Critique',
      'FLA avec 4+ matchs en 6 jours: Win rate -6.8% vs baseline',
      'xGoals FLA dans ces conditions: 2.74 vs moyenne 3.18 (-13.5%)',
      'xGoals concédés: 3.12 vs moyenne 2.81 (+11.0%)',
      'Bobrovsky SV% après fatigue: .908 vs .918 normal',
      'NYR: Repos 2 jours — Avantage récupération significatif',
    ],
    confidence: 78.9,
    tags: ['Fatigue', 'Calendrier', 'Contexte', 'Gardien'],
    timestamp: '20:58',
  },
  {
    id: '5',
    type: 'trend',
    title: 'Tendance ligue — Domination xGoals équipes Atlantique',
    summary: 'Analyse des 30 derniers jours révèle une domination statistique croissante des équipes de la division Atlantique. TOR, BOS, FLA affichent collectivement un xGoals différentiel de +0.67/match, le plus élevé depuis 2019-20.',
    details: [
      'Division Atlantique xGoals diff L30: +0.67/match (record 5 ans)',
      'TOR: +0.87 | BOS: +0.54 | FLA: +0.61 | MTL: +0.23',
      'Corrélation avec win rate: r=0.91 — Signal fort',
      'Tendance: Accélération depuis 15 jours (+0.12/match)',
      'Facteur: Amélioration PP% collective (+2.3% vs L60)',
      'Projection: Tendance maintenue pour 85% des modèles',
    ],
    confidence: 82.1,
    tags: ['Tendance ligue', 'Division Atlantique', 'xGoals', 'PP%'],
    timestamp: '20:30',
  },
];

const TYPE_CONFIG: Record<string, { label: string; color: string; bg: string; border: string; icon: string }> = {
  pre_game: { label: 'Pré-match', color: 'text-primary', bg: 'bg-info-subtle', border: 'border-primary/20', icon: 'DocumentTextIcon' },
  hidden_trend: { label: 'Tendance cachée', color: 'text-accent', bg: 'bg-accent/10', border: 'border-accent/20', icon: 'EyeIcon' },
  historical: { label: 'Historique', color: 'text-warning', bg: 'bg-warning-subtle', border: 'border-warning/20', icon: 'ClockIcon' },
  contextual: { label: 'Contextuel', color: 'text-positive', bg: 'bg-positive-subtle', border: 'border-positive/20', icon: 'LightBulbIcon' },
  trend: { label: 'Tendance', color: 'text-primary', bg: 'bg-info-subtle', border: 'border-primary/20', icon: 'ArrowTrendingUpIcon' },
};

export default function AIInsightsClient() {
  const [selectedType, setSelectedType] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>('1');
  const [generating, setGenerating] = useState(false);

  const filtered = selectedType === 'all' ? INSIGHTS : INSIGHTS.filter(i => i.type === selectedType);

  const handleGenerate = () => {
    setGenerating(true);
    setTimeout(() => setGenerating(false), 2000);
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Advanced AI Insights</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Analyses contextuelles avancées — Style hedge fund analytique
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="status-badge bg-primary/10 text-primary border border-primary/20 font-mono-data text-2xs">
            Modèle: Random Forest v2.1.0
          </span>
          <button onClick={handleGenerate} disabled={generating} className="btn-primary flex items-center gap-2">
            <Icon name="SparklesIcon" size={14} />
            {generating ? 'Génération...' : 'Générer insights'}
          </button>
        </div>
      </div>

      {/* Type filters */}
      <div className="flex flex-wrap gap-2">
        {['all', 'pre_game', 'hidden_trend', 'historical', 'contextual', 'trend'].map(type => (
          <button
            key={type}
            onClick={() => setSelectedType(type)}
            className={`status-badge text-2xs cursor-pointer transition-all ${selectedType === type ? 'bg-primary/20 text-primary border border-primary/30' : 'bg-muted text-muted-foreground border border-border hover:border-primary/20'}`}
          >
            {type === 'all' ? 'Tous' : TYPE_CONFIG[type]?.label || type}
          </button>
        ))}
      </div>

      {/* Insights */}
      <div className="space-y-4">
        {filtered.map((insight) => {
          const config = TYPE_CONFIG[insight.type];
          const isExpanded = expandedId === insight.id;
          return (
            <div key={insight.id} className={`card border ${config.border} transition-all`}>
              <button
                onClick={() => setExpandedId(isExpanded ? null : insight.id)}
                className="w-full p-4 text-left"
              >
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${config.bg}`}>
                    <Icon name={config.icon as Parameters<typeof Icon>[0]['name']} size={15} className={config.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`status-badge text-2xs ${config.bg} ${config.color} border ${config.border}`}>
                        {config.label}
                      </span>
                      {insight.match && (
                        <span className="status-badge bg-muted text-foreground border border-border text-2xs font-mono-data">
                          {insight.match}
                        </span>
                      )}
                      <span className={`ml-auto text-xs font-mono-data font-bold ${insight.confidence >= 80 ? 'text-positive' : insight.confidence >= 70 ? 'text-warning' : 'text-negative'}`}>
                        {insight.confidence}% conf.
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground mb-1">{insight.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{insight.summary}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {insight.tags.map(tag => (
                        <span key={tag} className="status-badge bg-muted text-muted-foreground border border-border text-2xs">{tag}</span>
                      ))}
                      <span className="text-2xs font-mono-data text-muted-foreground ml-auto">{insight.timestamp} ET</span>
                    </div>
                  </div>
                  <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={14} className="text-muted-foreground flex-shrink-0 mt-1" />
                </div>
              </button>

              {isExpanded && (
                <div className={`px-4 pb-4 border-t border-border/50 pt-3 ${config.bg}`}>
                  <p className="text-2xs font-medium uppercase tracking-wider text-muted-foreground mb-3">Analyse détaillée</p>
                  <div className="space-y-2">
                    {insight.details.map((detail, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className={`text-2xs font-mono-data ${config.color} flex-shrink-0 mt-0.5`}>→</span>
                        <p className="text-xs text-foreground leading-relaxed">{detail}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="flex-1 bg-muted rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full`} style={{ width: `${insight.confidence}%`, backgroundColor: insight.confidence >= 80 ? '#00C853' : insight.confidence >= 70 ? '#FFB800' : '#FF3B5C' }} />
                    </div>
                    <span className="text-2xs font-mono-data text-muted-foreground">Confiance: {insight.confidence}%</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* AI model info */}
      <div className="card p-4 bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="CpuChipIcon" size={14} className="text-primary" />
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Moteur analytique</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Modèle actif', value: 'Random Forest v2.1.0', color: 'text-primary' },
            { label: 'Précision globale', value: '74.3%', color: 'text-positive' },
            { label: 'Insights générés', value: '1,247', color: 'text-foreground' },
            { label: 'Tendances détectées', value: '23', color: 'text-accent' },
          ].map((stat) => (
            <div key={stat.label}>
              <p className="text-2xs text-muted-foreground uppercase tracking-wider">{stat.label}</p>
              <p className={`text-sm font-semibold font-mono-data mt-0.5 ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
